<template>
	<Dropdown
		ref="dropdown"
		:disabled="disabled"
		theme="status-dropdown"
		placement="bottom-start"
		:distance="4"
		:triggers="['click']"
		:auto-hide="true"
		@show="handleShow"
		@hide="handleHide"
	>
		<div
			:class="[
				$style['status-button'],
				{
					[$style[`status-button--${statusStyle}`]]: !disabled,
					[$style['status-button--shrinked']]: shrinked,
					[$style['status-button--disabled']]: disabled,
					[$style[`status-button--checked-${statusStyle}`]]: checked,
					[$style[`status-button--checked`]]: checked
				}]"
			:style="statusButtonStyle()"
		>
			{{ content || statusText }}
			<FontAwesomeIcon
				v-if="!disabled"
				:icon="['fal', 'angle-down']"
				size="lg"
			/>
		</div>
		<template #popper>
			<div :class="$style['dropdown__menu']">
				<div
					v-for="type in filteredList"
					:key="type.id"
					:class="[
						$style['status-button'],
						$style['status-button--full'],
						{
							[$style[`status-button--${type.style || type.id}`]]: !disabled,
							[$style['status-button--fitted']]: shrinked,
							[$style['status-button--disabled']]: disabled,
							[$style['status-button--hoverable']]: true

						}]"
					style="justify-content: space-between; margin-bottom: 5px;"
					@click.stop="handleStatusChange(type.id)"
				>
					{{ type.name }}
				</div>
				<div
					:class="$style['dropdown__close']"
					@click.stop="handleClose"
				>
					<FontAwesomeIcon
						v-if="!disabled"
						:icon="['far', 'times']"
						size="xs"
					/>
				</div>
			</div>
		</template>
	</Dropdown>
</template>

<script>
import { Dropdown } from 'floating-vue'
export default {
	name: 'BaseStatusDropdown',
	components: {
		Dropdown
	},
	model: {
		prop: 'status',
		event: 'change'
	},
	inject: {
		setBaseTableRowStatusDropdownOpen: {
			default: null
		}
	},
	props: {

		content: {
			type: [String, Number],
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		shrinked: {
			type: Boolean,
			default: false
		},
		checked: {
			type: Boolean,
			default: false
		},
		status: {
			type: [String],
			default: null
		},
		types: {
			type: Array,
			default: () => []
		}
	},
	computed: {

		statusStyle () {
			const targetItem = this.types.find(type => type.id === this.status)
			if (targetItem) {
				return targetItem.style || targetItem.id

			}
			return this.status
		},

		filteredList () {
			return this.types.filter(type => !type.disabled)
		},

		backupStatus () {
			switch (this.status) {
				case 'new':
					return this.$t('common.statuses.new')
				case 'processing':
					return this.$t('common.statuses.processing')
				case 'in_progress':
					return this.$t('common.statuses.processing')
				case 'done':
					return this.$t('common.statuses.done')
				case 'cancelled':
					return this.$t('common.statuses.declined')
				case 'declined':
					return this.$t('common.statuses.declined')
				case 'soft_red':
					return this.$t('common.statuses.soft_red')
				case 'reserved':
					return this.$t('common.statuses.reserved')
				case 'ready':
					return this.$t('common.statuses.ready')
				case 'neutral':
					return this.$t('common.statuses.ready')
				case 'draft_processing':
					return this.$t('common.statuses.draft-processing')
				case 'draft_completed':
					return this.$t('common.statuses.draft-completed')
				case 'draft_failed':
					return this.$t('common.statuses.draft-failed')
				case 'no_vin_info_found':
					return this.$t('common.statuses.no-vin-info-found')
				default:
					return '-'
			}
		},
		statusText () {
			const type = this.types.find(_type => _type.id === this.status)
			if (type) {
				return type.name
			}
			return this.backupStatus
		}
	},
	beforeDestroy () {
		if (this.setBaseTableRowStatusDropdownOpen) {
			this.setBaseTableRowStatusDropdownOpen(false)
		}
	},
	methods: {
		statusButtonStyle () {
			const { staticStyle, style } = this.$vnode.data || {}
			const styles = []

			if (staticStyle) {
				styles.push(staticStyle)
			}

			if (style) {
				if (Array.isArray(style)) {
					style.forEach(item => {
						if (item) styles.push(item)
					})
				} else if (typeof style === 'object') {
					styles.push(style)
				}
			}

			const camelize = (str) => str.replace(/-([a-z])/g, (_, c) => c.toUpperCase())

			const normalizeStyleObject = (obj) => Object.keys(obj).reduce((acc, key) => {
				acc[camelize(key)] = obj[key]
				return acc
			}, {})

			const merged = Object.assign({}, ...styles.map(normalizeStyleObject))

			const isSet = value => value !== null && value !== undefined && value !== '' && value !== 'auto'

			const hasWidth = isSet(merged.width)
			const hasMinWidth = isSet(merged.minWidth)
			const hasMaxWidth = isSet(merged.maxWidth)
			const hasHeight = isSet(merged.height)
			const hasMinHeight = isSet(merged.minHeight)
			const hasMaxHeight = isSet(merged.maxHeight)

			if (!hasWidth && !hasMinWidth && !hasMaxWidth && !hasHeight && !hasMinHeight && !hasMaxHeight) {
				return null
			}

			const result = {}

			if (hasWidth || hasMinWidth || hasMaxWidth) {
				if (hasWidth) {
					result.width = '100%'
				} else if (this.shrinked) {
					result.width = 'min-content'
				}

				if (hasMinWidth) {
					result.minWidth = merged.minWidth
				}

				if (hasMaxWidth) {
					result.maxWidth = merged.maxWidth
				}
			}

			if (hasHeight || hasMinHeight || hasMaxHeight) {
				if (hasHeight) {
					result.height = '100%'
				}

				if (hasMinHeight) {
					result.minHeight = merged.minHeight
				}

				if (hasMaxHeight) {
					result.maxHeight = merged.maxHeight
				}
			}

			if (!merged.whiteSpace) {
				result.whiteSpace = 'nowrap'
			}

			return result
		},
		handleShow () {
			if (this.setBaseTableRowStatusDropdownOpen) {
				this.setBaseTableRowStatusDropdownOpen(true)
			}
		},
		handleHide () {
			if (this.setBaseTableRowStatusDropdownOpen) {
				this.setBaseTableRowStatusDropdownOpen(false)
			}
		},
		handleStatusChange (status) {
			this.$emit('change', status)
			this.$refs['dropdown'].hide()
		},
		handleClose () {
			this.$refs['dropdown'].hide()
		}
	}

}
</script>

<style lang="scss" module>


	.dropdown {
		&__menu {
			position: relative;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			padding: $gutter;
			min-width: 130px;
			max-width: 100%;
			width: auto;
		}

		&__close {
			position: absolute;
			top: -5px;
			right: -5px;
			height: 17px;
			width: 17px;
			background-color: getColor(surface);
			border-radius: 50%;
			box-shadow: -1px 1px 2px 1px rgb(0, 0, 0, 15%);
			display: flex;
			align-items: center;
			justify-content: center;
			color: getColor(caption);
			transition: background-color 0.12s ease;

			&:hover {
				background-color: getColor(hover-overlay-strong);
			}
		}
	}

	.status-button {
		position: relative;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 2px 10px;
		border-radius: 30px;
		color: getColor(surface);
		font-size: 12px;
		box-sizing: border-box;
		// max-width: 100px;
		width: 100px;
		min-width: 90px;

		&--full {
			width: 100%;
			max-width: auto;
		}

		&--hoverable {
			&:hover {
				opacity: 0.8;
			}
		}

		&--disabled {
			// background-color: getColor(white);
			border: 1px solid getColor(dark-green);
			color: getColor(dark-green-text);
			padding: 2px;
			justify-content: center;
		}

		&--shrinked {
			width: min-content;
			// min-width: 0;
		}

		&--fitted {
			width: 100%;
			min-width: 100%;
		}

		&--new {
			background-color: getColor(status-new);
		}

		&--done {
			background-color: getColor(status-done);
		}

		&--ready {
			background-color: getColor(status-ready);
		}

		&--neutral {
			background-color: transparent;
			border: 1px solid getColor(dark-green);
			color: getColor(dark-green-text);
		}

		&--declined {
			background-color: getColor(danger);
		}

		&--soft_red {
			background-color: transparent;
			border: 1px solid getColor(danger);
			// color: rgba(black, 0.87);
			color: getColor(danger);
		}

		&--cancelled {
			background-color: getColor(danger);
		}

		&--reserved {
			color: getColor(surface);
			background-color: getColor(warning);
		}

		&--processing {
			color: getColor(surface);
			background-color: getColor(status-processing);
		}

		&--in_progress {
			color: getColor(surface);
			background-color: getColor(status-processing);
		}

		> svg {
			margin-top: 1px;
			margin-left: $gutter;
		}

		&--checked {
			cursor: default;
			// padding: 3px 10px;
			border: none;

			&-new {
				background-color: getColor(status-new);
				color: getColor(surface);
			}

			&-neutral {
				border: 1px solid getColor(dark-green);
				color: getColor(dark-green-text);
			}

			&-done {
				background-color: getColor(status-done);
				color: getColor(surface);
			}

			&-soft_red {
				background-color: transparent;
				border: 1px solid getColor(danger);
				// color: rgba(black, 0.87);
				color: getColor(danger);
			}

			&-declined {
				background-color: getColor(danger);
				color: getColor(surface);
			}

			&-cancelled {
				background-color: getColor(danger);
				color: getColor(surface);
			}

			&-ready {
				background-color: getColor(status-ready);
				color: getColor(surface);
			}

			&-reserved {
				color: getColor(surface);
				background-color: getColor(warning);
			}

			&-processing {
				background-color: getColor(status-processing);
				color: getColor(surface);
			}

			&-in_progress {
				background-color: getColor(status-processing);
				color: getColor(surface);
			}
		}
	}
</style>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.v-popper__popper.v-popper--theme-status-dropdown {
    z-index: 10000;
    max-width: 100vw;
    box-sizing: border-box;

    .v-popper__inner {
        padding: 0;
        background-color: getColor(surface-elevated);
        border-radius: 8px;
        box-shadow: 0 4px 12px 0 getColor(shadow);
        overflow: visible;
        box-sizing: border-box;
        max-width: calc(100vw - 32px);

        @include dark-mode {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px getColor(border-darker), 0 8px 24px rgb(0, 0, 0, 50%), 0 2px 8px rgb(0, 0, 0, 30%);
        }
    }

    .v-popper__arrow-container {
        display: none;
    }
}
/* stylelint-enable selector-class-pattern */
</style>
